import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { LoginModel } from 'src/app/models/loginModel';
import { RegisterModel } from 'src/app/models/registerModel';
import { SessionService } from 'src/app/services/session-service';
import { getOAuthApiOrigin } from 'src/environments/oauth-api-origin';

@Component({
  selector: 'pla-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css'],
})
export class RegisterComponent implements OnInit {
  public registerModel: RegisterModel = new RegisterModel();
  public registerErrors: string[] = [];
  public loading: boolean = false;

  public registerForm = new FormGroup({
    email: new FormControl(this.registerModel.email, [
      Validators.required,
      Validators.email,
    ]),
    fullName: new FormControl('', [
      Validators.required,
    ]),
    password: new FormControl(this.registerModel.password, [
      Validators.required,
    ]),
    passwordRepeat: new FormControl(this.registerModel.passwordRepeat, [
      Validators.required,
    ]),
  });

  constructor(
    private sessionService: SessionService,
    private snackbar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute
  ) {
    if (sessionService.isLoggedIn()) {
      this.router.navigate(['/account']);
    }
  }

  ngOnInit(): void {
    this.handleSocialCallback();
    this.initializeGoogleSignIn();
    this.initializeFacebookSDK();
  }

  private handleSocialCallback(): void {
    this.route.queryParamMap.subscribe((params) => {
      const social = params.get('social');
      const error = params.get('error');
      if (social !== 'apple') return;

      if (error) {
        this.snackbar.open(`Apple sign-in failed: ${error}`, 'Close', {
          duration: 9000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['social-auth-error-snackbar'],
        });
        return;
      }

      this.loading = true;
      this.sessionService.RefreshToken().subscribe(
        () => {
          this.router.navigate(['/account']);
        },
        () => {
          this.loading = false;
          this.snackbar.open('Apple sign-in could not be completed. Please try again.', 'Close', {
            duration: 9000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['social-auth-error-snackbar'],
          });
        }
      );
    });
  }

  public register(): void {
    if (this.registerForm.invalid) return;
    const { email, fullName, password, passwordRepeat } = this.registerForm.getRawValue();
    const nameParts = (fullName || '').trim().split(/\s+/);
    this.registerModel.email = email;
    this.registerModel.firstName = nameParts[0] || '';
    this.registerModel.lastName = nameParts.slice(1).join(' ') || nameParts[0] || '';
    this.registerModel.password = password;
    this.registerModel.passwordRepeat = passwordRepeat;
    this.registerModel.signupReason = 'standard';
    this.makeRegisterRequest(this.registerModel);
  }

  public login(): void {
    this.router.navigate(['/login']);
  }

  private initializeGoogleSignIn(): void {
    const checkGoogleLoaded = setInterval(() => {
      if (typeof (window as any).google !== 'undefined') {
        clearInterval(checkGoogleLoaded);
        (window as any).google.accounts.id.initialize({
          client_id: '505978889074-sj1une8r7955ofq2uasjq11g0eia8p6r.apps.googleusercontent.com',
          callback: (response: any) => this.handleGoogleCredentialResponse(response),
          auto_select: false,
        });
      }
    }, 100);

    setTimeout(() => { clearInterval(checkGoogleLoaded); }, 10000);
  }

  public googleLogin(): void {
    if (typeof (window as any).google === 'undefined') {
      this.snackbar.open('Google sign-up is not available. Please refresh and try again.', 'Close', { duration: 5000 });
      return;
    }
    (window as any).google.accounts.id.prompt();
  }

  private handleGoogleCredentialResponse(response: any): void {
    if (!response.credential) {
      this.snackbar.open('Google sign-up was cancelled or failed', 'Close', { duration: 5000 });
      return;
    }

    this.loading = true;
    this.sessionService.handleGoogleLogin(
      response.credential,
      'register-page',
      (success: boolean, message: string) => {
        if (!success) {
          this.snackbar.open(message, 'Close', { duration: 5000 });
          this.loading = false;
        } else {
          this.router.navigate(['/account']).then(() => {
            this.snackbar.open('Sign up complete!', 'Close', {
              duration: 3000,
            });
          });
        }
      }
    );
  }

  private initializeFacebookSDK(): void {
    // Wait for Facebook SDK to load
    const checkFacebookLoaded = setInterval(() => {
      if (typeof (window as any).FB !== 'undefined') {
        clearInterval(checkFacebookLoaded);
        this.configureFacebookSDK();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkFacebookLoaded);
    }, 10000);
  }

  private configureFacebookSDK(): void {
    (window as any).FB.init({
      appId: '1163605102162389',
      cookie: true,
      xfbml: true,
      version: 'v21.0'
    });
  }

  public facebookLogin(): void {
    if (typeof (window as any).FB === 'undefined') {
      this.snackbar.open('Facebook SDK not loaded. Please refresh and try again.', 'Close', { duration: 5000 });
      return;
    }

    (window as any).FB.login((response: any) => {
      if (response.authResponse) {
        this.handleFacebookAuthResponse(response.authResponse);
      } else {
        console.log('User cancelled Facebook login or did not fully authorize.');
      }
    }, { scope: 'public_profile,email' });
  }

  public appleLogin(): void {
    const authBaseUrl = getOAuthApiOrigin();
    const returnUrl = encodeURIComponent(`${window.location.origin}${window.location.pathname}`);
    window.location.href = `${authBaseUrl}/auth/apple?signupReason=register-page&returnUrl=${returnUrl}`;
  }

  private handleFacebookAuthResponse(authResponse: any): void {
    const accessToken = authResponse.accessToken;

    if (!accessToken) {
      this.snackbar.open('Facebook sign-up failed. Please try again.', 'Close', { duration: 5000 });
      return;
    }

    this.loading = true;
    this.sessionService.handleFacebookLogin(
      accessToken,
      'register-page',
      (success: boolean, message: string) => {
        if (!success) {
          this.snackbar.open(message, 'Close', { duration: 5000 });
          this.loading = false;
        } else {
          this.router.navigate(['/account']).then(() => {
            this.snackbar.open('Sign up complete!', 'Close', {
              duration: 3000,
            });
          });
        }
      }
    );
  }

  private makeRegisterRequest(registerDetails: RegisterModel): void {
    this.registerErrors = [];
    this.loading = true;
    this.sessionService.register(
      registerDetails,
      (success: boolean, message: string, errors: string[]) => {
        if (success) {
          this.router.navigate(['/login']).then(() => {
            this.snackbar.open('Sign up complete!', 'Close', {
              duration: 3000,
            });
          });
          this.makeLoginRequest(
            this.sessionService.registerToLogin(registerDetails)
          );
        } else {
          this.loading = false;
          if (errors.length > 0) {
            this.registerErrors = errors.slice(0, 1);
            this.snackbar.open(message, 'Close', { duration: 5000 });
          } else {
            this.snackbar.open('Unknown error occured', 'Close', {
              duration: 5000,
            });
          }
        }
      }
    );
  }

  private makeLoginRequest(loginDetails: LoginModel): void {
    this.sessionService.login(
      loginDetails,
      (success: boolean, message: string) => {
        if (!success) {
          this.snackbar.open(message, 'Close', { duration: 5000 });
        } else {
          this.router.navigate(['/account']);
        }
      }
    );
  }
}
